/**
 * Subscribe to the `roles` collection. Auto-seed system roles on first load
 * so legacy role strings ('owner', 'viewer', etc.) resolve to real docs.
 */
import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, setDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { SYSTEM_ROLES } from '../lib/permissions';

async function seedSystemRolesIfNeeded() {
  const snap = await getDocs(collection(db, 'roles'));
  const existing = new Set(snap.docs.map((d) => d.id));
  const missing = SYSTEM_ROLES.filter((r) => !existing.has(r.id));
  if (missing.length === 0) return;
  await Promise.all(
    missing.map((r) =>
      setDoc(doc(db, 'roles', r.id), {
        name: r.name,
        description: r.description,
        isSystem: r.isSystem,
        permissions: r.permissions,
        createdAt: new Date().toISOString(),
      })
    )
  );
}

export default function useRoles(user) {
  const [roles, setRoles] = useState([]);
  const [rolesById, setRolesById] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setRoles([]); setRolesById({}); setLoading(false);
      return;
    }
    let unsub;
    (async () => {
      try {
        await seedSystemRolesIfNeeded();
      } catch (e) {
        // Seeding may fail for non-owners due to rules — that's fine, roles will load via the listener
        console.warn('seed system roles:', e.message);
      }
      unsub = onSnapshot(
        collection(db, 'roles'),
        (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          const byId = {};
          list.forEach((r) => { byId[r.id] = r; });
          setRoles(list);
          setRolesById(byId);
          setLoading(false);
        },
        (err) => {
          console.error('roles listener:', err);
          setLoading(false);
        }
      );
    })();
    return () => { if (unsub) unsub(); };
  }, [user]);

  return { roles, rolesById, loading };
}
